import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useHistory } from "react-router";
import { useSetRecoilState } from "recoil";

import { CustomButton } from "@/components/Button";
import { buildDiagnosisFlowSteps } from "@/components/diagnosis/diagnosisFlowStepDefinitions";
import DiagnosisFlowSteps from "@/components/diagnosis/DiagnosisFlowSteps";
import { DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA } from "@/constants/account";
import { useSendAppEvent } from "@/hooks/useSendAppEvent";
import { RegistrationFlowLayout as RegistrationTemplate } from "@/layouts/RegistrationFlowLayout";
import theme from "@/theme";
import { AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { getStorageAccountRegistrationUserId, removeStorageAccountRegistrationState } from "@/utilities/storage";
import { UserRegistrationsAccoutFormState } from "@/view_models/atoms";

import CharacterImage from "@assets/image/pre_diagnosis_character.png";

// 最初の段階（投資力診断）に着手する画面。文言は4画面共通の定義（diagnosisFlowStepDefinitions）を使う
const INTRO_STEPS = buildDiagnosisFlowSteps(["active", "plain", "plain"]);

const useStyles = makeStyles(() => ({
  heading: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.4,
    textAlign: "center",
    whiteSpace: "pre-line",
  },
  subText: {
    fontSize: 14,
    lineHeight: 1.5,
    textAlign: "center",
    whiteSpace: "pre-line",
    marginTop: theme.spacing(1.5),
  },
  characterWrap: {
    display: "flex",
    justifyContent: "center",
    margin: theme.spacing(3, 0),
  },
  characterImage: {
    width: 180,
    height: "auto",
  },
  bottomGroup: {
    marginTop: 32,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  bottomLeadTitle: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    textAlign: "center",
    marginBottom: theme.spacing(2.5),
  },
  choiceRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    width: "100%",
  },
}));

export const RegistrationIntroPage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const setAccountFormData = useSetRecoilState(UserRegistrationsAccoutFormState);

  useSendAppEvent(AppEventTokenEnum.InvestmentExperience);

  const onSelectExperience = React.useCallback(
    (hasExperience: boolean) => {
      const isOwnedDraft = getStorageAccountRegistrationUserId() !== null;
      if (isOwnedDraft) removeStorageAccountRegistrationState();
      setAccountFormData((prev) => ({
        ...(isOwnedDraft ? DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA : prev),
        has_investment_experience: hasExperience,
      }));
      history.push("/pre_diagnosis");
    },
    [history, setAccountFormData]
  );

  return (
    <RegistrationTemplate>
      <Typography className={classes.heading}>{"不動産投資の始め方が\n3ステップでわかります"}</Typography>
      <Typography className={classes.subText}>
        {"質問に答えるだけで、\nあなたに合った進め方を整理できます。"}
      </Typography>

      <Box className={classes.characterWrap}>
        <img src={CharacterImage} alt="診断キャラクター" className={classes.characterImage} />
      </Box>

      <DiagnosisFlowSteps steps={INTRO_STEPS} />

      <Box className={classes.bottomGroup}>
        <Typography className={classes.bottomLeadTitle}>不動産投資のご経験はありますか？</Typography>
        <Box className={classes.choiceRow}>
          <CustomButton buttonSize="L" type="button" tone="outline" onClick={() => onSelectExperience(true)}>
            ある
          </CustomButton>
          <CustomButton buttonSize="L" type="button" tone="outline" onClick={() => onSelectExperience(false)}>
            ない
          </CustomButton>
        </Box>
      </Box>
    </RegistrationTemplate>
  );
};
